'use client';

import { useEffect, useState } from 'react';
import { Clock, LogOut } from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { useLogout } from '@/shared/hooks/apis/auth/useLogout';
import { useSessionIdleGuard } from '@/shared/hooks/useSessionIdleGuard';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { authStorage } from '@/shared/libs/authStorage';
import { formatCountdown, type IdleTimeoutSetting } from '@/shared/libs/sessionIdle';
import { useAuthStore } from '@/shared/store/authStore';

/**
 * L'avertissement avant coupure de session, et l'entretien qui la précède (suivora-api#887).
 *
 * Monté une fois pour toute l'application authentifiée. La redirection n'est pas faite ici : la
 * session vidée, la sentinelle de chaque espace renvoie vers **son** écran de connexion —
 * `/login`, `/admin/login` ou `/console/login`. Choisir la destination ici enverrait un
 * gestionnaire sur le portail consultant.
 */
export function SessionIdleWarning({ setting }: { setting: IdleTimeoutSetting }) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clearSession = useAuthStore(s => s.clearSession);
  const logout = useLogout();
  const [staying, setStaying] = useState(false);

  const { phase, keepAlive } = useSessionIdleGuard(setting, isAuthenticated);
  const expired = phase.kind === 'expired';

  /**
   * L'échéance dont l'avertissement a déjà été écarté.
   *
   * Ne concerne que la fin absolue : elle ne se repousse pas, donc la fenêtre doit pouvoir se
   * fermer — sans quoi elle **empêcherait** d'aller enregistrer son travail, qui est précisément
   * ce qu'elle demande de faire. Mémorisée par échéance et non par un simple booléen : une session
   * suivante aurait sa propre fin, et hériterait sinon d'un écart qui ne la concerne pas.
   */
  const [dismissedDeadline, setDismissedDeadline] = useState<number | null>(null);

  useEffect(() => {
    if (!expired) return;
    /*
      La coupure est prononcée ici plutôt que subie au prochain appel.

      Le serveur la prononcera de toute façon, mais au premier renouvellement — c'est-à-dire à
      l'enregistrement, sur un formulaire rempli. La prononcer maintenant ne fait rien perdre de
      plus, et remplace une erreur incompréhensible en fin de saisie par un écran de connexion.
    */
    authStorage.clearTokens();
    clearSession();
  }, [expired, clearSession]);

  const absolute = phase.kind === 'warn' && phase.limit === 'absolute';
  const open = phase.kind === 'warn' && !(absolute && dismissedDeadline === phase.deadline);

  const stay = async () => {
    setStaying(true);
    try {
      const ok = await keepAlive();
      // Renouvellement refusé : la session était déjà fermée côté serveur. Inutile d'insister,
      // et surtout inutile de laisser croire qu'elle a été prolongée.
      if (!ok) {
        authStorage.clearTokens();
        clearSession();
      }
    } finally {
      setStaying(false);
    }
  };

  return (
    <Modal
      open={open}
      /*
        Sur l'inactivité, aucune fermeture implicite : ni Échap, ni clic à côté. Un geste réflexe
        qui fait disparaître la fenêtre sans prolonger la session la rendrait pire qu'inutile — on
        croirait avoir répondu.

        Sur la fin absolue, l'inverse : rien ne peut la repousser, et la fenêtre doit s'écarter pour
        laisser enregistrer. La retenir ferait perdre exactement ce qu'elle prétend sauver.
      */
      onClose={() => {
        if (phase.kind === 'warn' && phase.limit === 'absolute') {
          setDismissedDeadline(phase.deadline);
        }
      }}
      closeOnEsc={absolute}
      closeOnOverlayClick={absolute}
      ariaLabel={t('session.idle.title', 'Votre session va se fermer')}
      className="w-full max-w-md rounded-xl bg-white shadow-lg"
    >
      <Modal.Body className="flex flex-col gap-4 p-6">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Clock className="size-5" />
          </span>
          <div>
            <h2 className="text-text-strong-950 text-lg font-semibold">
              {absolute
                ? t('session.max.title', 'Votre session atteint sa durée maximale')
                : t('session.idle.title', 'Votre session va se fermer')}
            </h2>
            {/*
              Deux textes, parce que les deux motifs n'appellent pas le même geste. Dire « vous
              allez être déconnecté » sans dire quoi faire laisse chercher un bouton qui n'existe
              pas — sur une échéance qui, elle, ne se négocie pas.
            */}
            <p className="text-text-sub-600 mt-1 text-sm leading-relaxed">
              {absolute
                ? t(
                    'session.max.body',
                    'Votre organisation limite la durée d’une connexion. Cette échéance ne peut pas être repoussée : enregistrez votre travail, puis reconnectez-vous.',
                  )
                : t(
                    'session.idle.body',
                    'Vous serez déconnecté pour inactivité. Une saisie en cours qui n’a pas été enregistrée serait perdue.',
                  )}
            </p>
          </div>
        </div>

        <p
          className="text-text-strong-950 text-center font-mono text-3xl font-bold tabular-nums"
          aria-live="polite"
        >
          {formatCountdown(phase.kind === 'warn' ? phase.msLeft : 0)}
        </p>
      </Modal.Body>

      <Modal.Footer>
        {/*
          Pas de « Rester connecté » sur la fin absolue : rien ne la repousse, et un bouton qui ne
          tient pas sa promesse coûte plus cher que son absence — on l'aurait cliqué au lieu d'aller
          enregistrer.
        */}
        {absolute ? (
          <Button onClick={() => phase.kind === 'warn' && setDismissedDeadline(phase.deadline)}>
            <span className="block text-white">
              {t('session.max.acknowledge', 'Enregistrer mon travail')}
            </span>
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => logout.mutate()} disabled={staying}>
              <LogOut className="size-4 text-black" />
              <span className="block text-black">{t('session.idle.logout', 'Se déconnecter')}</span>
            </Button>
            <Button onClick={stay} disabled={staying}>
              <span className="block text-white">{t('session.idle.stay', 'Rester connecté')}</span>
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
